import React from "react";
import { ArrowRight } from "lucide-react";
import { STEPS, STEP_TAGS, WHY12X } from "../mock/data";
import { Wrap, SectionHead, Note } from "./Section";
import { useReveal } from "../hooks/useReveal";

function StepCard({ s, i }) {
  const [ref, inView] = useReveal();
  return (
    <div ref={ref} className={`reveal ${inView ? "in" : ""} card rounded-2xl p-6`} style={{ animationDelay: `${(i % 4) * 80}ms` }}>
      <div className="display text-[var(--muted)]/40 text-3xl mb-4">{s.n}</div>
      <h3 className="display text-base mb-3">{s.title}</h3>
      <p className="text-[13px] text-[var(--muted)] leading-relaxed">{s.body}</p>
    </div>
  );
}

export default function Steps() {
  return (
    <div>
      <Wrap>
        <SectionHead
          tag="From nothing to connected"
          title="Eight steps,"
          highlight="in order"
          desc="Nothing happens automatically and nothing is signed before you have seen the whole picture. This is the full sequence, including the parts that involve your own decision."
        />

        <div className="mt-10 flex flex-wrap items-center gap-2">
          {STEP_TAGS.map((t, i) => (
            <React.Fragment key={t}>
              <span className="label text-[var(--text)] border border-white/[0.09] rounded-lg px-3 py-2 bg-white/[0.02]">{t}</span>
              {i < STEP_TAGS.length - 1 && <span className="text-[var(--muted)]/40">—</span>}
            </React.Fragment>
          ))}
        </div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => <StepCard key={i} s={s} i={i} />)}
        </div>

        <div className="mt-8 card rounded-2xl p-6 max-w-3xl">
          <p className="text-[14px] text-[var(--muted)] leading-relaxed">
            <span className="text-[var(--text)] font-medium">Step two is the one that matters.</span> The webinar covers the mechanics, the historical numbers and the risks in full — attend it before deciding anything.
          </p>
          <a href="#join" className="mt-4 inline-flex items-center gap-2 label text-accent hover:gap-3 transition-all">
            Join the community <ArrowRight size={13} />
          </a>
        </div>
      </Wrap>

      <Wrap>
        <SectionHead
          tag="The obvious question"
          title="Why would anyone let you trade"
          highlight="12× your deposit?"
          desc="Because the additional trading capital comes from external liquidity providers, not out of the broker's own pocket — and because two hard conditions make their maximum loss knowable in advance."
        />
        <div className="mt-8 card rounded-2xl p-6 max-w-3xl">
          <div className="label text-accent mb-2">What it is not</div>
          <p className="text-[14px] text-[var(--muted)] leading-relaxed">
            This is not a loan to you and it is not leverage you have to repay. You cannot end up owing money on an amplified account: the structure closes itself before that point, and your maximum loss is the capital you deposited.
          </p>
        </div>
        <div className="mt-8 grid md:grid-cols-2 gap-5">
          {WHY12X.map((w, i) => (
            <div key={i} className="card rounded-2xl p-7">
              <div className="mono text-accent text-sm mb-4">{w.n}</div>
              <h3 className="display text-xl mb-3">{w.title}</h3>
              <p className="text-[14px] text-[var(--muted)] leading-relaxed">{w.body}</p>
            </div>
          ))}
        </div>
        <Note>
          Account structures, lock-in terms and drawdown limits are set and administered by the broker and may change. Confirm the current terms with TAG Markets before depositing.
        </Note>
      </Wrap>
    </div>
  );
}
